// Generic entity interpolation system for smooth client-side rendering

import type { Vec2 } from "@game/core";

export interface InterpolatableEntity {
  id: string;
  pos?: Vec2;
  vel?: Vec2;
  rotation?: number;
}

export interface InterpolationConfig {
  positionSmoothing: number;
  rotationSmoothing: number;
  useWorldWrap: boolean;
}

export interface World {
  w: number;
  h: number;
}

export const INTERPOLATION_CONFIGS: Record<string, InterpolationConfig> = {
  player: {
    positionSmoothing: 0.25,
    rotationSmoothing: 0.3,
    useWorldWrap: true,
  },
  bullet: {
    positionSmoothing: 0.4,
    rotationSmoothing: 0.5,
    useWorldWrap: true,
  },
  asteroid: {
    positionSmoothing: 0.2,
    rotationSmoothing: 0.25,
    useWorldWrap: true,
  },
  pellet: {
    positionSmoothing: 0.15,
    rotationSmoothing: 0.2,
    useWorldWrap: true,
  },
  mine: {
    positionSmoothing: 0.2,
    rotationSmoothing: 0.25,
    useWorldWrap: true,
  },
};

export function wrappedDistance(
  a: number,
  b: number,
  worldSize: number,
): number {
  let delta = b - a;

  if (Math.abs(delta) > worldSize / 2) {
    delta = delta > 0 ? delta - worldSize : delta + worldSize;
  }

  return delta;
}

export function interpolateAngle(
  current: number,
  target: number,
  factor: number,
): number {
  const normalizedCurrent =
    ((current % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
  const normalizedTarget =
    ((target % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);

  let delta = normalizedTarget - normalizedCurrent;

  if (delta > Math.PI) {
    delta -= 2 * Math.PI;
  } else if (delta < -Math.PI) {
    delta += 2 * Math.PI;
  }

  const interpolated = normalizedCurrent + delta * factor;

  return ((interpolated % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
}

export function interpolateEntity<T extends InterpolatableEntity>(
  current: T,
  target: T,
  config: InterpolationConfig,
  world: World,
): T {
  if (!current.pos || !target.pos) {
    return target;
  }

  const dx = config.useWorldWrap
    ? wrappedDistance(current.pos.x, target.pos.x, world.w)
    : target.pos.x - current.pos.x;

  const dy = config.useWorldWrap
    ? wrappedDistance(current.pos.y, target.pos.y, world.h)
    : target.pos.y - current.pos.y;

  const newX = current.pos.x + dx * config.positionSmoothing;
  const newY = current.pos.y + dy * config.positionSmoothing;

  const wrappedX = config.useWorldWrap
    ? ((newX % world.w) + world.w) % world.w
    : newX;
  const wrappedY = config.useWorldWrap
    ? ((newY % world.h) + world.h) % world.h
    : newY;

  const newRotation =
    current.rotation !== undefined && target.rotation !== undefined
      ? interpolateAngle(
          current.rotation,
          target.rotation,
          config.rotationSmoothing,
        )
      : target.rotation;

  return {
    ...target,
    pos: { x: wrappedX, y: wrappedY },
    rotation: newRotation,
  };
}

export function interpolateEntities<T extends InterpolatableEntity>(
  targets: Map<string, T>,
  current: Map<string, T>,
  config: InterpolationConfig,
  world: World,
): Map<string, T> {
  const interpolated = new Map<string, T>();

  for (const [id, targetEntity] of targets) {
    const currentEntity = current.get(id);

    if (currentEntity === undefined) {
      interpolated.set(id, { ...targetEntity });
    } else {
      const interpolatedEntity = interpolateEntity(
        currentEntity,
        targetEntity,
        config,
        world,
      );
      interpolated.set(id, interpolatedEntity);
    }
  }

  return interpolated;
}
